import type { PictureSet } from "@/lib/images";
import {
  blogAlbumCardPicture,
  blogAlbumFeaturedPicture,
  blogAlbumLcpHref,
  blogFdcCardPicture,
  blogFdcFeaturedPicture,
  blogFdcLcpHref,
  blogHistoryCardPicture,
  blogHistoryFeaturedPicture,
  blogHistoryLcpHref,
  blogMagnifierCardPicture,
  blogMagnifierFeaturedPicture,
  blogMagnifierLcpHref,
  blogPerforationsCardPicture,
  blogPerforationsFeaturedPicture,
  blogPerforationsLcpHref,
  blogSoakingCardPicture,
  blogSoakingFeaturedPicture,
  blogSoakingLcpHref,
} from "@/lib/images";

export const SITE_URL = "https://carolinamountainstamps.com";
export const EBAY_STORE_URL = "https://www.ebay.com/str/carolinamountainstamps";

export type BlogCta = {
  heading: string;
  body: string;
  href: string;
  label: string;
  external?: boolean;
};

export type InlinePart = string | { text: string; href: string } | { strong: string };

export type BlogBlock =
  | { kind: "p"; content: InlinePart[] }
  | { kind: "h3"; text: string }
  | { kind: "ul"; items: InlinePart[][] }
  | { kind: "ol"; items: InlinePart[][] };

export type BlogSection = {
  heading: string;
  paragraphs: string[];
  blocks?: BlogBlock[];
};

export type BlogPost = {
  slug: string;
  title: string;
  excerpt: string;
  metaTitle?: string;
  metaDescription: string;
  readMinutes?: number;
  date: string;
  dateLabel: string;
  tag: string;
  imageAlt: string;
  ogImage: string;
  lcpHref: string;
  cardPicture: PictureSet;
  featuredPicture: PictureSet;
  sections: BlogSection[];
  cta: BlogCta;
};

export const posts: BlogPost[] = [
  {
    slug: "how-to-remove-stamps-from-envelopes",
    title: "How to Remove Stamps from Envelopes Without Damaging Them",
    excerpt:
      "Soaking stamps off paper is simple when you know the steps. Here is how to remove used stamps safely, and when you should leave them on the envelope.",
    metaTitle: "How to Remove Stamps from Envelopes Safely | Carolina Mountain Stamps",
    metaDescription:
      "Learn how to soak and remove stamps from envelopes without damage. Step-by-step tips from a Hendersonville, NC stamp shop.",
    readMinutes: 4,
    date: "2026-10-02",
    dateLabel: "October 2, 2026",
    tag: "Care & handling",
    imageAlt:
      "Used vintage postage stamps soaking in a shallow dish of water beside stamp tongs and stamps drying on a paper towel",
    ogImage: `${SITE_URL}/images/blog/how-to-remove-stamps-from-envelopes.webp`,
    lcpHref: blogSoakingLcpHref,
    cardPicture: blogSoakingCardPicture,
    featuredPicture: blogSoakingFeaturedPicture,
    sections: [
      {
        heading: "",
        paragraphs: [
          "Many collections begin with a stack of old envelopes. Removing those stamps the right way protects their condition and their value. A torn corner or a thinned back can turn a nice stamp into a space filler, so it pays to be patient.",
        ],
      },
      {
        heading: "First, Decide Whether to Remove It at All",
        paragraphs: [],
        blocks: [
          {
            kind: "p",
            content: [
              "Not every stamp should come off its envelope. Leave stamps on the paper if the envelope is a first day cover, has an interesting postmark or cachet, or shows unusual postal history. In those cases, the whole envelope is the collectible. (Learn more in our guide, ",
              {
                text: "What Are First Day Covers and Why Do Collectors Love Them?",
                href: "/blog/what-are-first-day-covers",
              },
              ".)",
            ],
          },
          {
            kind: "p",
            content: [
              "Never soak unused stamps with original gum. Water removes the gum, and collectors pay a premium for it.",
            ],
          },
        ],
      },
      {
        heading: "What You Will Need",
        paragraphs: [],
        blocks: [
          {
            kind: "ul",
            items: [
              ["A shallow bowl or dish of lukewarm water"],
              ["Stamp tongs (fingers leave oils that can stain)"],
              ["Paper towels or blotting paper"],
              ["A heavy book for pressing"],
            ],
          },
        ],
      },
      {
        heading: "Step-by-Step: Soaking Stamps",
        paragraphs: [],
        blocks: [
          { kind: "h3", text: "1. Trim the Envelope" },
          {
            kind: "p",
            content: [
              "Cut around each stamp, leaving a small margin of paper. Do not cut too close, or you risk clipping the perforations.",
            ],
          },
          { kind: "h3", text: "2. Sort Before Soaking" },
          {
            kind: "p",
            content: [
              "Separate stamps on colored or heavily printed envelopes and soak them on their own. Dyes can bleed and stain every stamp in the bowl.",
            ],
          },
          { kind: "h3", text: "3. Soak Face-Up" },
          {
            kind: "p",
            content: [
              "Float the stamps face-up in lukewarm water. Most will begin to release within 10 to 20 minutes. Let the water do the work and never pull a stamp off the paper.",
            ],
          },
          { kind: "h3", text: "4. Lift Gently with Tongs" },
          {
            kind: "p",
            content: [
              "Once a stamp slides away freely, lift it out with tongs. If any gum remains on the back, give it a quick rinse in clean water.",
            ],
          },
          { kind: "h3", text: "5. Dry Face-Down" },
          {
            kind: "p",
            content: [
              "Lay the stamps face-down on a paper towel. After they are damp-dry, place them between clean paper towels under a heavy book overnight. This keeps them flat and prevents curling.",
            ],
          },
        ],
      },
      {
        heading: "A Note on Self-Adhesive Stamps",
        paragraphs: [
          "Modern self-adhesive stamps can be tricky. Many do not release cleanly in water and can be damaged by soaking. When in doubt, leave them on a neatly trimmed piece of envelope. Plenty of collectors mount them that way.",
        ],
      },
      {
        heading: "Common Mistakes to Avoid",
        paragraphs: [],
        blocks: [
          {
            kind: "ul",
            items: [
              ["Using hot water, which can fade colors"],
              ["Peeling stamps off before they float free"],
              ["Drying stamps face-up, which can cause them to stick to the towel"],
              ["Handling wet stamps with your fingers"],
            ],
          },
        ],
      },
      {
        heading: "Questions About Your Stamps?",
        paragraphs: [
          "If you are working through a box of old envelopes and are not sure what to keep on paper, we are happy to help. Browse our collections or reach out to Carolina Mountain Stamps anytime.",
        ],
      },
    ],
    cta: {
      heading: "Not sure what to keep on paper?",
      body: "Bring a box of old envelopes to the Hendersonville booth, or write to us. We are happy to help you sort what should stay on the cover.",
      href: "/#collections",
      label: "Browse our collections",
    },
  },
  {
    slug: "stamp-perforations-and-watermarks-explained",
    title: "Stamp Perforations and Watermarks Explained: How Collectors Identify Look-Alike Stamps",
    excerpt:
      "Two stamps can look identical and still be very different issues. Perforation gauges and watermarks are how collectors tell them apart.",
    metaTitle: "Stamp Perforations and Watermarks Explained | Carolina Mountain Stamps",
    metaDescription:
      "Learn how to measure stamp perforations and find watermarks. A simple guide to identifying look-alike stamps from a Hendersonville, NC stamp shop.",
    readMinutes: 5,
    date: "2026-10-02",
    dateLabel: "October 2, 2026",
    tag: "Identification",
    imageAlt:
      "Vintage postage stamp on a perforation gauge beside a black watermark tray and magnifying loupe",
    ogImage: `${SITE_URL}/images/blog/stamp-perforations-and-watermarks-explained.webp`,
    lcpHref: blogPerforationsLcpHref,
    cardPicture: blogPerforationsCardPicture,
    featuredPicture: blogPerforationsFeaturedPicture,
    sections: [
      {
        heading: "",
        paragraphs: [
          "Have you ever found two stamps with the same design, color, and value, and wondered if they are the same? Often they are not. Small details like perforations and watermarks can separate a common stamp from a more desirable variety. Learning to check them is one of the most useful skills a collector can build.",
        ],
      },
      {
        heading: "What Are Perforations?",
        paragraphs: [],
        blocks: [
          {
            kind: "p",
            content: [
              'Perforations are the rows of small holes punched between stamps so they can be torn apart. The little points left on the edge of the stamp are called "teeth." Early stamps had no perforations and had to be cut apart with scissors. In the United States, perforated stamps arrived in 1857.',
            ],
          },
          { kind: "h3", text: "Why Perforation Measurements Matter" },
          {
            kind: "p",
            content: [
              "Printers used different perforating machines over the years. The same stamp design may exist in more than one perforation size, and catalogs list them as separate varieties. Sometimes one is common and the other is scarce.",
            ],
          },
          { kind: "h3", text: "How to Measure Perforations" },
          {
            kind: "p",
            content: [
              "Collectors measure perforations with a perforation gauge. The measurement is the number of holes (or teeth) within 2 centimeters.",
            ],
          },
          {
            kind: "ol",
            items: [
              ["Place the stamp on the gauge."],
              [
                "Slide it until the teeth line up perfectly with the dots or lines on one row of the gauge.",
              ],
              ['Read the number beside that row, such as "Perf 11" or "Perf 12."'],
              [
                'Measure both the top and the sides. Some stamps have different perforations on each, written as "Perf 11 x 10½."',
              ],
            ],
          },
        ],
      },
      {
        heading: "What Are Watermarks?",
        paragraphs: [],
        blocks: [
          {
            kind: "p",
            content: [
              "A watermark is a design pressed into the paper when it was made. It is thinner than the surrounding paper and usually cannot be seen from the front. Governments used watermarks to help prevent counterfeiting. Some U.S. stamps from the late 1890s through the 1910s were printed on watermarked paper, which is why watermarks are important for identifying many issues from that era.",
            ],
          },
          { kind: "h3", text: "How to Find a Watermark" },
          {
            kind: "ul",
            items: [
              [
                { strong: "Hold it to the light." },
                " Sometimes you can see the watermark by holding the stamp up to a lamp.",
              ],
              [
                { strong: "Use a watermark tray." },
                " Place the stamp face-down in a black tray. The watermark may appear as darker lines.",
              ],
              [
                { strong: "Use watermark fluid." },
                " A few drops of fluid made for stamps can make the pattern visible for a moment, then evaporate without harming most stamps. Always follow the product's instructions and avoid using fluid on stamps with printing that may run.",
              ],
            ],
          },
        ],
      },
      {
        heading: "Putting It All Together",
        paragraphs: [],
        blocks: [
          {
            kind: "p",
            content: [
              "When identifying a stamp, compare the design, color, perforations, and watermark with your catalog. Each detail narrows down the exact issue. Condition still matters, too. For more on what drives value, read ",
              {
                text: "How to Tell If Your Old Stamps Are Worth Anything",
                href: "/blog/how-to-tell-if-old-stamps-are-worth-anything",
              },
              ".",
            ],
          },
        ],
      },
      {
        heading: "Need a Second Opinion?",
        paragraphs: [
          "If you have stamps that look alike and you are not sure which variety you have, Carolina Mountain Stamps is glad to help. Reach out or browse our collections to see examples.",
        ],
      },
    ],
    cta: {
      heading: "Two stamps, one question",
      body: "If you are not sure which variety you have, send a clear photograph. We are glad to take a second look.",
      href: "mailto:saca@ix.netcom.com?subject=Stamp%20identification",
      label: "Email us a find",
    },
  },
  {
    slug: "history-of-united-states-postage-stamps",
    title: "A Brief History of U.S. Postage Stamps: From 1847 to Today",
    excerpt:
      "From two stamps in 1847 to thousands of designs today, here is how U.S. postage stamps evolved and why each era offers something special for collectors.",
    metaTitle: "A Brief History of U.S. Postage Stamps (1847 to Today) | Carolina Mountain Stamps",
    metaDescription:
      "From the first 1847 Franklin and Washington stamps to today's Forever stamps, explore the history of U.S. postage stamps and what it means for collectors.",
    readMinutes: 5,
    date: "2026-10-02",
    dateLabel: "October 2, 2026",
    tag: "Stamp history",
    imageAlt:
      "Antique letter with wax seal surrounded by vintage 19th-century style postage stamps and a fountain pen",
    ogImage: `${SITE_URL}/images/blog/history-of-united-states-postage-stamps.webp`,
    lcpHref: blogHistoryLcpHref,
    cardPicture: blogHistoryCardPicture,
    featuredPicture: blogHistoryFeaturedPicture,
    sections: [
      {
        heading: "",
        paragraphs: [
          "Every stamp tells a story, and the history of U.S. postage is full of them. Understanding how stamps changed over time helps collectors appreciate what they own and choose an era to focus on.",
        ],
      },
      {
        heading: "Before Stamps",
        paragraphs: [
          "Before postage stamps, mail was often paid for by the person receiving it. Rates depended on distance and the number of sheets of paper, which made mailing a letter confusing and expensive. Great Britain introduced the world's first adhesive postage stamp, the Penny Black, in 1840, and the idea quickly spread.",
        ],
      },
      {
        heading: "1847: The First U.S. Stamps",
        paragraphs: [
          "The United States issued its first official postage stamps on July 1, 1847: a 5-cent stamp picturing Benjamin Franklin and a 10-cent stamp picturing George Washington. Franklin was honored as the nation's first Postmaster General. These early stamps were imperforate and had to be cut apart with scissors.",
        ],
      },
      {
        heading: "The 1850s and 1860s: Perforations and Wartime Changes",
        paragraphs: [
          "Perforated stamps appeared in 1857, making stamps much easier to separate. The Civil War brought major changes, and the post office issued new designs during the conflict. Stamps from this period are popular with collectors interested in American history.",
        ],
      },
      {
        heading: "1893: The First Commemoratives",
        paragraphs: [
          "The Columbian Exposition stamps of 1893 were the first U.S. commemorative stamps, celebrating the 400th anniversary of Columbus's voyage. They began a tradition of honoring people, places, and events that continues today.",
        ],
      },
      {
        heading: "The Early 1900s: Airmail and New Technology",
        paragraphs: [
          "As the country modernized, so did its stamps. Airmail service began in 1918, along with dedicated airmail stamps. Coil stamps, booklets, and new printing methods made stamps easier to produce and use.",
        ],
      },
      {
        heading: "Mid-Century: A Golden Age of Commemoratives",
        paragraphs: [
          "From the 1930s onward, the U.S. issued many colorful commemorative stamps covering national parks, famous Americans, historic anniversaries, and more. These stamps are widely available today, which makes them a fun and affordable area for new collectors.",
        ],
      },
      {
        heading: "Modern Stamps: Self-Adhesives and Forever Stamps",
        paragraphs: [
          "Self-adhesive stamps became widespread in the 1990s, replacing the need to lick the back of a stamp. In 2007, the Forever stamp was introduced. It is always valid for the current first-class letter rate, no matter when it was bought.",
        ],
      },
      {
        heading: "Choosing an Era to Collect",
        paragraphs: [],
        blocks: [
          {
            kind: "p",
            content: [
              "Many collectors focus on one period, such as classic 19th-century issues, early commemoratives, or modern stamps. If you are just starting out, our ",
              {
                text: "Beginner's Guide to Starting a Stamp Collection",
                href: "/blog/beginners-guide-to-starting-a-stamp-collection",
              },
              " can help you choose a focus.",
            ],
          },
        ],
      },
      {
        heading: "Explore U.S. Stamps from Every Era",
        paragraphs: [
          "Carolina Mountain Stamps offers a curated selection of U.S. and worldwide stamps, from early issues to souvenir sheets and plate blocks. Browse our collections, visit our eBay store, or send us your want list.",
        ],
      },
    ],
    cta: {
      heading: "Stamps from every era, chosen by hand",
      body: "Look through our curated eBay store for United States and worldwide issues, from early stamps to souvenir sheets and plate blocks.",
      href: EBAY_STORE_URL,
      label: "Browse the eBay store",
      external: true,
    },
  },
  {
    slug: "beginners-guide-to-starting-a-stamp-collection",
    title: "A Beginner's Guide to Starting a Stamp Collection",
    excerpt:
      "How to choose a focus, gather a few essential tools, find stamps, and store them so the collection lasts — from a Hendersonville shop that has helped beginners for twenty years.",
    metaDescription:
      "Start a stamp collection: choose a focus, gather supplies, find stamps, and store them well. Beginner lots from Hendersonville, NC.",
    date: "2026-08-08",
    dateLabel: "August 8, 2026",
    tag: "Getting started",
    imageAlt:
      "Open stamp album page with rows of vintage postage stamps and philatelic tongs resting on the page",
    ogImage: `${SITE_URL}/images/blog/beginners-guide.webp`,
    lcpHref: blogAlbumLcpHref,
    cardPicture: blogAlbumCardPicture,
    featuredPicture: blogAlbumFeaturedPicture,
    sections: [
      {
        heading: "",
        paragraphs: [
          "The first stamp that ever stopped you was probably not a rarity. It was a color, a tiny engraved portrait, a scene small enough to hide in a pocket. That flicker of attention is how most collections begin — including the ones we have helped fill from our shop in Hendersonville for more than twenty years.",
          "You do not need a vault or a catalogue memorized. You need a focus, a few honest tools, and the habit of treating paper as if it might still be here in fifty years. Here is how we suggest a beginner begin.",
        ],
      },
      {
        heading: "Choose a Focus Before You Buy",
        paragraphs: [
          "The fastest way to feel lost is to buy a little of everything. Philately rewards patience, and a theme gives that patience a shape.",
          "United States or worldwide. U.S. collecting is a natural first path if you live here: catalogues are easy to find, issues are well documented, and you can grow from modern commemoratives into earlier numbers as your eye improves. Worldwide collecting is broader and often more affordable by the packet — our Pick-a-Packet booth exists for exactly that kind of browsing.",
          "Era. Some people fall for classic engraved work from the nineteenth and early twentieth centuries. Others prefer the color and topics of postwar commemoratives. You can collect used stamps that actually traveled on letters, or mint issues that never saw the mail. Neither is more “correct.” Used material is usually gentler on a beginner’s budget; mint never-hinged stamps ask more of storage and of your wallet.",
          "Topic. Birds, ships, space, Christmas, national parks, Royal Wedding souvenirs — topical collecting is how a lot of families stay interested together. A topic also makes a small budget feel complete. Fifty well-chosen stamps on one subject will teach you more than five hundred random ones in a cigar box.",
          "Write the focus down. It will save you from impulse lots that do not belong in your album.",
        ],
      },
      {
        heading: "Essential Supplies for a New Collector",
        paragraphs: [
          "A beginner’s kit should stay small.",
          "Stockbooks. A stockbook with clear strips is the kindest first home for stamps. You can move things around as your focus sharpens, without committing to a printed album page you may outgrow.",
          "Tongs. Always tongs — never fingers if you can help it. Skin oil stains gum and dulls paper. A pair of spade-tip stamp tongs costs little and will last decades.",
          "A magnifier. Ten-power is plenty for reading inscriptions, checking perforations, and seeing whether a cancel is light or heavy. You will use it more than you expect.",
          "Hinges versus mounts. Hinges are inexpensive paper tabs for used stamps, traditional and perfectly respectable for a working collection. Mounts — clear pockets — protect mint gum and are what we recommend once you start keeping unused stamps. Do not soak or peel a mint stamp to “improve” it. Leave the gum alone.",
          "A watermark tray and a perforation gauge can wait. Learn your stamps first.",
        ],
      },
      {
        heading: "Where to Find Stamps",
        paragraphs: [
          "Start close. Attics, inherited albums, and old correspondence still turn up interesting material in the mountains of North Carolina. Bring those finds in. We will help you sort wheat from chaff — no pressure, and no lecture.",
          "Stamp shows, club meetings, and a shop you can walk into are the next step. Being able to look at paper in person teaches condition faster than any screen. Our booth at Needful Things Mall on Route 64 is built for that kind of looking: books, boxes, and time.",
          "Mail-order and online lots are excellent once you know what you want. Read descriptions, look at scans of the actual stamps, and buy from sellers who grade honestly.",
          "We keep beginner-friendly lots in our eBay store for exactly this stage — mixed United States and world material, priced so you can learn without a painful mistake.",
        ],
      },
      {
        heading: "How to Handle and Store Them Properly",
        paragraphs: [
          "Work on a clean, dry table. Hold stamps with tongs at the edge, not across the face. Never use household tape, glue, or self-adhesive photo corners from a craft drawer. Those adhesives migrate, stain, and become a conservation problem later.",
          "Keep collections away from attics, basements, and sunny windows. Heat, humidity, and light are the quiet enemies. A closet shelf in a climate-controlled room is better than a decorative stack on a mantel.",
          "Leave stamps on cover if the envelope is interesting — especially first day covers and older postmarks. Soaking is for common used stamps on scrap paper, and even then, test a corner first. Some inks run. Some papers are too thin to survive a bath.",
        ],
      },
      {
        heading: "Begin With Something You Would Be Glad to Own",
        paragraphs: [
          "A collection is a long conversation with paper. Start small, handle it well, and let the focus deepen. When you are ready for beginner-friendly lots chosen by people who have done this for more than twenty years, browse our curated eBay store. We are here in Hendersonville, and in the inbox, when you have questions.",
        ],
      },
    ],
    cta: {
      heading: "Beginner-friendly lots, chosen by hand",
      body: "Browse our curated eBay store for mixed United States and worldwide stamps priced for new collectors — the same care we bring to the Hendersonville booth.",
      href: EBAY_STORE_URL,
      label: "Browse the eBay store",
      external: true,
    },
  },
  {
    slug: "what-are-first-day-covers",
    title: "What Are First Day Covers and Why Do Collectors Love Them?",
    excerpt:
      "A first day cover is a stamp on an envelope cancelled the day it was issued. Here is why cachets, cancellations, and condition make collectors hunt for them.",
    metaDescription:
      "What first day covers are, why collectors love them, and how cachets and cancels affect value. Shop FDCs from Hendersonville, NC.",
    date: "2026-08-18",
    dateLabel: "August 18, 2026",
    tag: "Covers",
    imageAlt:
      "Classic United States first day cover envelope with a cachet, commemorative stamp, and First Day of Issue cancellation",
    ogImage: `${SITE_URL}/images/blog/first-day-covers.webp`,
    lcpHref: blogFdcLcpHref,
    cardPicture: blogFdcCardPicture,
    featuredPicture: blogFdcFeaturedPicture,
    sections: [
      {
        heading: "",
        paragraphs: [
          "If you have ever held an envelope cancelled on the day a stamp was born, you already know the small thrill of a first day cover. The stamp, the postmark, and often a picture printed on the left sit together as a record of a moment. Collectors shorten the name to FDC. We have carried them for as long as we have carried stamps, because they are where philately and postal history shake hands.",
        ],
      },
      {
        heading: "What a First Day Cover Is",
        paragraphs: [
          "A first day cover is an envelope, postal card, or similar item bearing a new stamp and cancelled on the first day that stamp was authorized for use. In the United States, that cancellation often reads “First Day of Issue,” with the city and date of the ceremony. The stamp is not an afterthought on the envelope. It is the reason the envelope exists.",
          "Many FDCs also carry a cachet: a printed or hand-drawn illustration that comments on the stamp’s subject. The cachet is not the postmark. Anyone can design a cachet; only a post office can apply an official first-day cancel. Together they make a small, complete document — stamp, story, and date.",
          "Uncacheted covers exist too, and early ones can be scarce. A plain envelope with a genuine first-day cancel is still an FDC. The art on the left is extra, not the definition.",
        ],
      },
      {
        heading: "A Short History of the First Day Cover",
        paragraphs: [
          "People have saved first-day mail for as long as new stamps have been news. The hobby as we know it took shape in the 1920s, when collectors began preparing envelopes in advance and sending them to the issuing city for the official cancel. George W. Linn is often credited with popularizing the cacheted FDC in 1923, for the Harding memorial issue — a black-bordered design that collectors still talk about.",
          "By the 1930s and 1940s, cachet makers had become names in their own right. ArtCraft, House of Farnam, Fleetwood, Artmaster, and later Colorano silk cachets each had a look. Some collectors follow one maker; others chase a topic: space, presidents, national parks, statehood, Christmas.",
          "The Postal Service eventually formalized first-day ceremonies, pictorial cancels, and a standard “First Day of Issue” killer. Covers can be serviced after the printed date, depending on policy, which is why a knowledgeable seller’s description still matters.",
        ],
      },
      {
        heading: "What Makes Certain FDCs Valuable",
        paragraphs: [
          "Not every first day cover is a treasure, and that is a mercy. Most modern mass-produced FDCs sell for a few dollars. The ones collectors hunt for tend to share a few traits.",
          "Cachets. Hand-painted and limited-run cachets — Dorothy Knapp, for example, or small-press work — can dwarf the value of a printed production cover for the same stamp. Early cachets from the 1920s and 1930s, especially from makers who did not produce thousands, are always in demand. A cachet that is merely pretty is not automatically scarce.",
          "Cancellations. A clear, well-struck first-day cancel that ties the stamp to the cover — the postmark touching the stamp — is the ideal. Smudged, faint, or later “favor” cancels are less desirable. Pictorial cancels and unofficial city cancels from the first day can add interest for specialists.",
          "Condition. The envelope should be clean, unbent, and free of stains, foxing, and heavily toned paper. Stamps should be well centered if the issue allows it, with intact perforations. Pencil notes on the back are common and usually forgiven; tape and labels on the front are not.",
          "Scarcity and story. Classic first days, limited events, and covers connected to a genuine piece of postal history — an actual first-day ceremony, a small town, a short-lived rate — rise above the decorative modern envelope. As with stamps, the catalogue is a guide, not a promise.",
        ],
      },
      {
        heading: "Tips for Collecting First Day Covers",
        paragraphs: [
          "Choose a lane. One cachet maker, one topic, one decade, or United States only. A focused FDC collection looks like a library. An unfocused one looks like a mailbag.",
          "Store covers upright in archival cover albums, not stacked under books. Avoid PVC. Keep them dry and out of the sun, the same as stamps.",
          "Learn to read a description. “Uncacheted,” “addressed,” “planted cancel,” “set of four” — these words matter. Ask for a scan of the actual cover, front and back.",
          "Do not soak a stamp off a cover to “improve” it. You will usually destroy more value than you create. The cover is the collectible.",
        ],
      },
      {
        heading: "A Curated Selection, Not a Warehouse",
        paragraphs: [
          "We carry a curated selection of first day covers — United States and worldwide, event covers among them — in the Hendersonville booth and in our eBay store. We are not trying to own every cachet ever printed. We are trying to keep good paper in front of people who will care for it.",
          "If you are building a topical FDC collection, or you have inherited a box of ceremony covers and want a second pair of eyes, write to us. After twenty years, we still like opening an envelope that already has a story printed on it.",
        ],
      },
    ],
    cta: {
      heading: "First day covers, chosen with care",
      body: "Look through our curated eBay store for covers, cachets, and event mail — the same selection we keep at Needful Things Mall in Hendersonville.",
      href: EBAY_STORE_URL,
      label: "Shop first day covers",
      external: true,
    },
  },
  {
    slug: "how-to-tell-if-old-stamps-are-worth-anything",
    title: "How to Tell If Your Old Stamps Are Worth Anything",
    excerpt:
      "Rarity, centering, gum, errors, and plate blocks all affect what old stamps are worth. A practical guide from a family that has been identifying collections for 20+ years.",
    metaDescription:
      "Learn how rarity, condition, errors, and grading affect stamp value. Our Hendersonville family shop has helped collectors for 20+ years.",
    date: "2026-08-28",
    dateLabel: "August 28, 2026",
    tag: "Value & grading",
    imageAlt:
      "Brass magnifying glass over vintage United States postage stamps including Washington and Franklin issues",
    ogImage: `${SITE_URL}/images/blog/stamp-value.webp`,
    lcpHref: blogMagnifierLcpHref,
    cardPicture: blogMagnifierCardPicture,
    featuredPicture: blogMagnifierFeaturedPicture,
    sections: [
      {
        heading: "",
        paragraphs: [
          "Almost every family in the mountains has a cigar box, a cookie tin, or a forgotten album somewhere. Someone saved stamps because they were pretty, or because a relative worked at the post office. Then the question arrives, usually after a closet is cleaned: are these worth anything?",
          "Sometimes yes. Often the value is modest, and that is still a collection worth keeping. The honest answer depends on a handful of factors we have been reading for more than twenty years — not on a single internet photograph.",
        ],
      },
      {
        heading: "Rarity Comes First — But It Is Not the Whole Story",
        paragraphs: [
          "A stamp is not valuable because it is old. Nineteenth-century stamps were printed in the millions. Plenty of 1890s issues are still common. Rarity is about how many survive in the condition collectors want, and how many people want them.",
          "Look for early United States issues in sound condition, stamps from short printings, and material nobody thought to save. Scott Catalogue Value is a trade reference; selling prices often sit below it. We price most of our stock about 20 percent under SCV for that reason.",
          "A common commemorative from the 1940s, even unused, may be worth a few cents. A well-centered classic with original gum may be worth a conversation.",
        ],
      },
      {
        heading: "Condition: Centering, Gum, and Hinging",
        paragraphs: [
          "Condition is where beginners are most often surprised. Two copies of the same stamp can differ in price by a factor of ten.",
          "Centering. Collectors pay for stamps whose design sits evenly inside the perforations. “Jumbo” margins are a bonus. Designs cut by the perforations are space-fillers unless the stamp is rare enough that any copy is wanted.",
          "Gum. For unused stamps, original gum matters. Mint never-hinged (MNH) commands a premium on many twentieth-century issues. Disturbed gum, glazed gum, or gum that has been soaked and regummed — a genuine problem in the hobby — will reduce value, sometimes sharply. If you are not sure, do not try to “fix” the gum.",
          "Hinging. A light hinge mark on an older stamp is often acceptable and historically normal. On modern mint stamps, a hinge can drop the stamp into a cheaper grade. Used stamps are judged by the cancel: a light, readable cancel is usually preferred to a heavy obliteration.",
          "Paper. Thins, tears, creases, stains, pinholes, and pulled perforations all count. Hold the stamp to a light with tongs. A thin that you can see as a brighter spot is a fault.",
        ],
      },
      {
        heading: "Errors and Varieties",
        paragraphs: [
          "This is the part of the hobby that makes newspaper stories, and it is also the part most often misunderstood. A true error — inverted center, missing color, imperforate between where it should be perforated — can be valuable. So can certain listed varieties: double transfers, watermarks, coil waste, booklet panes with particular tabs.",
          "Most “odd-looking” stamps are not errors. They are color changelings from sunlight, stamps that got wet, or printer’s waste that never should have left the shop. If you think you have an error, do not soak it, do not trim it, and do not list it for sale until someone who handles this material every week has looked at it.",
          "We are happy to look. Identification is part of what a family shop is for.",
        ],
      },
      {
        heading: "Plate Blocks, Souvenir Sheets, and the Company a Stamp Keeps",
        paragraphs: [
          "Value sometimes lives in the format, not the single.",
          "Plate blocks — blocks of stamps with the plate number in the margin — are a traditional U.S. collecting field. We keep post-1939 U.S. mint never-hinged plate blocks by want list, priced under catalogue. Older plate blocks, especially well centered, are another matter and should be kept intact.",
          "Souvenir sheets and miniature sheets can be more desirable as complete panes than as singles torn out. Please do not separate a sheet until you know what you have. Booklets and booklet panes have their own specialists.",
          "Covers can be worth more than the stamps on them. A common stamp on an uncommon envelope is still an uncommon envelope.",
        ],
      },
      {
        heading: "Why Professional Grading Matters",
        paragraphs: [
          "Third-party grading exists because the difference between original gum and a clever regum is not always visible in a phone snapshot. Certificates from recognized expert committees matter for expensive stamps. For a tin of mixed commemoratives, they would be an expensive way to learn that you have postage.",
          "That is the friendly note we give every week in Hendersonville: most inherited lots are worth something as a collection, even when they are not worth a fortune as an investment. The pleasure of the album is real. So is the occasional surprise.",
          "Our family has served collectors for over twenty years. If you have a find and you want it identified — a plate block, a souvenir sheet, a cover, a single that looks “too good” — email us a clear photograph or bring it to the Needful Things Mall booth. We will tell you what we see. No pressure, and no reason to be embarrassed by a box of pretty ten-cent stamps. Those are how most of us started.",
        ],
      },
    ],
    cta: {
      heading: "Found something in a drawer?",
      body: "We have been identifying collections for more than twenty years. Send a photograph or stop by the Hendersonville booth — we are glad to help.",
      href: "mailto:saca@ix.netcom.com?subject=Stamp%20identification",
      label: "Email us a find",
    },
  },
];

export function getPostBySlug(slug: string) {
  return posts.find((p) => p.slug === slug);
}

export function getRelatedPosts(slug: string) {
  return posts.filter((p) => p.slug !== slug);
}

function inlineToString(part: InlinePart) {
  if (typeof part === "string") return part;
  if ("href" in part) return part.text;
  return part.strong;
}

export function wordCount(post: BlogPost) {
  const chunks = post.sections.flatMap((section) => {
    const fromBlocks = (section.blocks ?? []).flatMap((block) => {
      if (block.kind === "h3") return [block.text];
      if (block.kind === "p") return [block.content.map(inlineToString).join("")];
      return block.items.map((item) => item.map(inlineToString).join(""));
    });
    return [...section.paragraphs, ...fromBlocks];
  });
  return chunks.join(" ").trim().split(/\s+/).length;
}

export function readingMinutes(post: BlogPost) {
  if (post.readMinutes) return post.readMinutes;
  return Math.max(1, Math.round(wordCount(post) / 200));
}

export function listedPosts() {
  return [...posts].sort((a, b) => {
    if (a.date === b.date) return 0;
    return a.date < b.date ? 1 : -1;
  });
}
